import { useRef, type ReactNode } from "react";

import { cx } from "class-variance-authority";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Rectangle,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";

import { useElementSize } from "../../hooks/useElementSize";
import { rawRadius, rawSpacing } from "../../styles/theme.css";
import {
    AXIS_CHAR_WIDTH,
    buildAxisTickLabels,
    getLabelStride,
} from "../../utils/chartAxis";

import * as theme from "./GroupedBarChart.css";
import { GroupedBarChartTooltip } from "./GroupedBarChartTooltip";
import {
    GroupedBarChartBar,
    GroupedBarChartBucket,
    GroupedBarChartSeries,
} from "./types";

// Namespaced so the x axis key cannot collide with a series key.
const BUCKET_LABEL_KEY = "groupedBarChartBucketLabel";

const CHART_MARGIN = {
    top: rawSpacing[4],
    right: rawSpacing[2],
    bottom: 0,
    left: 0,
};
const Y_AXIS_WIDTH = 44;
const Y_AXIS_TICK_COUNT = 3;
const MAX_BAR_SIZE = 56;
const BAR_GAP = 4;
// recharts insets the band on *both* sides, so the bars share 64% of it.
const BAR_CATEGORY_GAP = "18%";
const BAR_RADIUS: [number, number, number, number] = [
    rawRadius.sm,
    rawRadius.sm,
    0,
    0,
];
export interface GroupedBarChartProps {
    series: GroupedBarChartSeries[];
    data: GroupedBarChartBucket[];
    height?: number;
    /** Never called for a bar that scored zero — there is nothing there to hit. */
    onBarClick?: (bucketIndex: number, series: GroupedBarChartSeries) => void;
    renderTooltip?: (bar: GroupedBarChartBar) => ReactNode;
}

/** Clustered bars — one group per bucket, one bar per series. */
export function GroupedBarChart({
    series,
    data,
    height = 220,
    onBarClick,
    renderTooltip,
}: GroupedBarChartProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const { width } = useElementSize(containerRef);

    const rows = data.map((bucket) => ({
        [BUCKET_LABEL_KEY]: bucket.label,
        ...Object.fromEntries(
            series.map(({ key }) => [key, bucket.values[key] ?? 0])
        ),
    }));

    const maxValue = Math.max(
        0,
        ...data.flatMap((bucket) =>
            series.map(({ key }) => bucket.values[key] ?? 0)
        )
    );
    // recharts draws no ticks at all for an all-zero domain.
    const yAxisMax = maxValue === 0 ? Y_AXIS_TICK_COUNT - 1 : "auto";

    // recharts spreads a capped bar across the band of a lone bucket.
    const barSize = data.length === 1 ? MAX_BAR_SIZE : undefined;

    // Only every stride-th label is printed; they would overlap otherwise.
    const labels = data.map(({ label }) => label);
    const plotWidth = Math.max(0, (width ?? 0) - Y_AXIS_WIDTH);
    const bucketWidth = data.length > 0 ? plotWidth / data.length : plotWidth;
    const labelStride = getLabelStride(labels, bucketWidth);

    return (
        <div
            className={cx(
                theme.container,
                onBarClick !== undefined && theme.clickable
            )}
            ref={containerRef}
        >
            <ResponsiveContainer width="100%" height={height}>
                <BarChart
                    data={rows}
                    margin={CHART_MARGIN}
                    barGap={BAR_GAP}
                    barCategoryGap={BAR_CATEGORY_GAP}
                    maxBarSize={MAX_BAR_SIZE}
                    barSize={barSize}
                >
                    <CartesianGrid vertical={false} />
                    <XAxis
                        dataKey={BUCKET_LABEL_KEY}
                        type="category"
                        axisLine={false}
                        tickLine={false}
                        // Already thinned below; recharts must not hide any on top of that.
                        interval={0}
                        ticks={buildAxisTickLabels(labels, labelStride, [
                            data.length - 1,
                        ])}
                        tick={
                            <GroupedBarChartAxisTick plotRight={width ?? 0} />
                        }
                    />
                    <YAxis
                        width={Y_AXIS_WIDTH}
                        domain={[0, yAxisMax]}
                        tickCount={Y_AXIS_TICK_COUNT}
                        allowDecimals={false}
                        axisLine={false}
                        tickLine={false}
                        className={theme.axisTick}
                    />
                    <Tooltip
                        // Per bar, not per bucket.
                        shared={false}
                        content={
                            <GroupedBarChartTooltip
                                series={series}
                                renderContent={renderTooltip}
                            />
                        }
                    />
                    {series.map((entry) => {
                        const handleClick = (_bar: unknown, index: number) => {
                            // recharts still draws a zero bar, flat on the baseline.
                            const value = data[index]?.values[entry.key] ?? 0;
                            if (value > 0) {
                                onBarClick?.(index, entry);
                            }
                        };

                        return (
                            <Bar
                                key={entry.key}
                                dataKey={entry.key}
                                name={entry.label}
                                fill={entry.color}
                                radius={BAR_RADIUS}
                                shape={
                                    <Rectangle
                                        data-pendo={entry["data-pendo"]}
                                    />
                                }
                                onClick={
                                    onBarClick === undefined
                                        ? undefined
                                        : handleClick
                                }
                                // Animation replays on every resize, which reads as a glitch.
                                isAnimationActive={false}
                            />
                        );
                    })}
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}

interface GroupedBarChartAxisTickProps {
    plotRight: number;
    // Supplied by recharts when it clones this element.
    x?: number;
    y?: number;
    payload?: { value?: string };
}

// recharts' own tick wraps onto several lines and runs off the plot at the ends.
function GroupedBarChartAxisTick({
    plotRight,
    x = 0,
    y = 0,
    payload,
}: GroupedBarChartAxisTickProps) {
    const label = payload?.value ?? "";
    // Nudged inwards rather than re-anchored, which would walk it into its neighbour.
    const halfWidth = (label.length * AXIS_CHAR_WIDTH) / 2;
    const clampedX = Math.min(Math.max(x, halfWidth), plotRight - halfWidth);

    return (
        <text
            x={clampedX}
            y={y}
            dy={14}
            textAnchor="middle"
            className={theme.axisTickHorizontal}
        >
            {label}
        </text>
    );
}
